import { Alert, Stack } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import type { AlertColor } from "@material-ui/core/Alert";

type TemplateSystem = "old" | "new";

type BulkMessageDialogMessageTipsProps = {
  message: string;
  templateSystem: TemplateSystem;
};

interface MessageTip {
  key: string;
  severity: AlertColor;
}

// Constructed via RegExp to avoid TS es5 target error with the `u` flag on regex literals
const emojiRegex = new RegExp("\\p{Emoji_Presentation}", "gu");

function getEmojiCount(text: string): number {
  const matches = text.match(emojiRegex);
  return matches?.length ?? 0;
}

function containsPhoneNumber(text: string): boolean {
  return /05\d[\d\-]{6,8}/.test(text) || /\+?\d{1,3}[\d\-\s]{7,14}/.test(text);
}

function containsUrl(text: string): boolean {
  return /https?:\/\/\S+/i.test(text) || /www\.\S+/i.test(text);
}

function containsNameField(text: string, templateSystem: TemplateSystem): boolean {
  if (templateSystem === "old") return text.includes("{שם פרטי}");
  return text.includes("{{callerFirstName}}");
}

export function BulkMessageDialogMessageTips({
  message,
  templateSystem
}: BulkMessageDialogMessageTipsProps): JSX.Element | null {
  const { t } = useTranslation();

  if (!message?.trim()) return null;

  const tips: MessageTip[] = [];

  if (!containsNameField(message, templateSystem)) {
    tips.push({
      key: t("contactsPage.bulkMessage.messageTips.missingNameField"),
      severity: "info"
    });
  }
  if (message.length > 300) {
    tips.push({
      key: t("contactsPage.bulkMessage.messageTips.tooLong"),
      severity: "warning"
    });
  }
  if (getEmojiCount(message) > 1) {
    tips.push({
      key: t("contactsPage.bulkMessage.messageTips.tooManyEmojis"),
      severity: "warning"
    });
  }
  if (containsPhoneNumber(message)) {
    tips.push({
      key: t("contactsPage.bulkMessage.messageTips.containsPhoneNumber"),
      severity: "error"
    });
  }
  if (containsUrl(message)) {
    tips.push({
      key: t("contactsPage.bulkMessage.messageTips.containsUrl"),
      severity: "error"
    });
  }

  if (!tips.length) return null;

  return (
    <Stack gap={1}>
      {tips.map((tip, index) => (
        <Alert key={index} severity={tip.severity} variant="outlined" sx={{ py: 0 }}>
          {tip.key}
        </Alert>
      ))}
    </Stack>
  );
}
